import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  Users,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  MessageSquare,
  MoreVertical,
  Eye
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export default function DoctorPatients() {
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    setLoading(true);
    try {
      // Simulated data
      setPatients([
        { id: 1, name: 'John Smith', email: 'john.smith@email.com', phone: '+1 234 567 8900', country: 'USA', condition: 'Cardiac Bypass', status: 'Active', lastVisit: '2024-01-15', treatmentValue: 850000 },
        { id: 2, name: 'Maria Garcia', email: 'maria.g@email.com', phone: '+34 612 345 678', country: 'Spain', condition: 'Knee Replacement', status: 'Completed', lastVisit: '2024-01-10', treatmentValue: 450000 },
        { id: 3, name: 'Ahmed Hassan', email: 'ahmed.h@email.com', phone: '+971 50 123 4567', country: 'UAE', condition: 'Spinal Surgery', status: 'In Treatment', lastVisit: '2024-01-18', treatmentValue: 1200000 },
        { id: 4, name: 'Sarah Johnson', email: 'sarah.j@email.com', phone: '+1 555 987 6543', country: 'Canada', condition: 'Hip Replacement', status: 'Follow-up', lastVisit: '2024-01-12', treatmentValue: 550000 },
        { id: 5, name: 'Li Wei', email: 'li.wei@email.com', phone: '+86 138 1234 5678', country: 'China', condition: 'Heart Valve', status: 'Active', lastVisit: '2024-01-17', treatmentValue: 950000 },
      ]);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'In Treatment': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'Follow-up': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const filteredPatients = patients.filter(patient => {
    const matchesSearch = patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         patient.condition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || patient.status === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6" data-testid="doctor-patients">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Patients</h1>
          <p className="text-gray-500 text-sm mt-1">{patients.length} total patients</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search patients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {['all', 'Active', 'In Treatment', 'Completed', 'Follow-up'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-teal-600 hover:bg-teal-700' : ''}
            >
              {f === 'all' ? 'All' : f}
            </Button>
          ))}
        </div>
      </div>

      {/* Patients Table */}
      <Card className="border shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No patients found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Patient</TableHead>
                    <TableHead className="min-w-[200px] hidden md:table-cell">Contact</TableHead>
                    <TableHead className="min-w-[120px]">Condition</TableHead>
                    <TableHead className="min-w-[100px]">Status</TableHead>
                    <TableHead className="min-w-[120px] hidden lg:table-cell">Treatment Value</TableHead>
                    <TableHead className="min-w-[100px] hidden sm:table-cell">Last Visit</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPatients.map((patient) => (
                    <TableRow key={patient.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 flex-shrink-0">
                            <AvatarFallback className="bg-teal-100 text-teal-700">
                              {patient.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{patient.name}</p>
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" /> {patient.country}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="space-y-1">
                          <p className="text-sm flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" /> {patient.email}
                          </p>
                          <p className="text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" /> {patient.phone}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-gray-900 text-sm">{patient.condition}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(patient.status)} border text-xs`}>
                          {patient.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <p className="font-semibold text-gray-900">{formatCurrency(patient.treatmentValue)}</p>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <p className="text-sm text-gray-500">{patient.lastVisit}</p>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <FileText className="w-4 h-4 mr-2" /> Medical Records
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <MessageSquare className="w-4 h-4 mr-2" /> Send Message
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Calendar className="w-4 h-4 mr-2" /> Schedule Appointment
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
